import React, { useEffect, useState } from "react";
import { useTranslate } from "@obsbot/remo-i18n";
import { Dropdown, Popover } from "antd";
import "./index.less";
import {
    CustomType,
    ICustomPayload,
    ICustomPayloadDescription,
    IServiceTextChat,
} from "@obsbot/remo-services";
import { ContactItem } from ".";
import {
    useChannelRoomStore,
    useChannelStore,
    useFriendStore,
    useGlobalStore,
} from "@obsbot/remo-stores";
import PersonalModal from "./PersonalModal";
import { InviteToChannel } from "./InviteToChannel";
import { eventBus } from "@obsbot/remo-utils";
import {
    AddFriend,
    blockFriend,
    cancelBlock,
    REMO_GAME_WEB_URL,
    setInviteURLTime,
} from "@obsbot/remo-server-api";
import { ReportModal } from "../../ReportModal";
import { CustomModal } from "../../CustomModal";

export enum ContactType {
    Friend = "Friend",
    Stranger = "Stranger",
    System = "System",
}

export interface RightMenuItem {
    label: string | React.ReactNode;
    key: string;
    showType: ContactType[];
    method: () => void;
}
export interface PrivateContactorProps {
    conversation: ContactItem; // Conversation对象
    rtm: IServiceTextChat;
    open: boolean;
    menuPosition: {
        x: number;
        y: number;
    }; // 菜单的位置
    onClickMenu: (show: boolean) => void;
}
export const PrivateRightMenu: React.FC<PrivateContactorProps> = ({
    conversation,
    open,
    onClickMenu,
    menuPosition,
    rtm,
}) => {
    const t = useTranslate();
    const { userInfo } = useGlobalStore();
    const { updateChannelInviteFlag } = useChannelStore();
    const { updateLocaleConversation, localeConversation } = useFriendStore();
    const [curType, setCurType] = useState<ContactType>();
    // const [showPersonal, setShowPersonal] = useState<boolean>(false);
    const [showInvite, setShowInvite] = useState<boolean>(false);
    const [showReport, setShowReport] = useState<boolean>(false);
    const [showCustomModal, setShowCustomModal] = useState<boolean>(false);
    const { blockedUsers, updateBlockedUsers } = useChannelRoomStore();

    const [customContent, setCustomContent] = useState({
        key: "",
        title: "",
        descPrev: "",
        descNext: "",
    });

    const menuItem: RightMenuItem[] = [
        {
            label: (
                <Popover
                    align={{ offset: [30, -16] }}
                    arrow={false}
                    classNames={{
                        root: "personal-card-popover",
                    }}
                    content={<PersonalModal conversation={conversation} />}
                    getPopupContainer={() => document.body}
                    placement="rightTop"
                >
                    {t("chat.view-profile")}
                </Popover>
            ),
            key: "ViewProfile",
            showType: [ContactType.Friend, ContactType.Stranger],
            method: () => {
                // setShowPersonal(true);
            },
        },
        {
            label: t("chat.add-friend"),
            key: "AddFriend",
            showType: [ContactType.Stranger],
            method: async () => {
                const res = await AddFriend({
                    receiverId: conversation.userID,
                    senderNickname: userInfo?.nickname || "",
                    senderLogo: userInfo?.logo || "",
                    relationType: 0, // 0-普通好友 1-情侣 2-闺蜜 3-兄弟
                    blockMsgBox: false,
                });
                if (res.code === "200") {
                    eventBus.emit("show-message", {
                        message: t("chat.add-friend-success"),
                        type: "success",
                    });
                } else {
                    eventBus.emit("show-message", {
                        message: t("chat.add-friend-failed"),
                        type: "error",
                    });
                }
            },
        },
        {
            label: t("chat.invite-channel"),
            key: "InviteChannel",
            showType: [ContactType.Friend, ContactType.Stranger],
            method: () => {
                if (blockedUsers.includes(conversation.userID)) {
                    eventBus.emit("show-message", {
                        message: t("error_code.RM.20005"),
                        type: "warning",
                    });
                    return;
                }
                setShowInvite(true);
            },
        },
        {
            label: t("chat.hidden-msg"),
            key: "HiddenMsg",
            showType: [ContactType.System],
            method: async () => {
                if (conversation?.conversationID) {
                    // 不显示会话框但保留聊天记录
                    await rtm.deleteConversation(conversation.conversationID, false);
                }
            },
        },
        {
            label:
                conversation.messageRemindType === "AcceptNotNotify"
                    ? t("chat.cancel-remind-msg")
                    : t("chat.remind-msg"),
            key: "RemindMsg",
            showType: [ContactType.System],
            method: async () => {
                if (conversation?.conversationID) {
                    const curType =
                        conversation.messageRemindType === "AcceptNotNotify"
                            ? "AcceptAndNotify"
                            : "AcceptNotNotify";
                    await rtm.setMsgRemindType([conversation.userID], curType);
                }
            },
        },
        {
            label: t("chat.clear-all-msg"),
            key: "ClearAllMsg",
            showType: [ContactType.System, ContactType.Friend, ContactType.Stranger],
            method: async () => {
                if (conversation?.conversationID) {
                    if (
                        localeConversation.find(
                            e => e.conversationID === conversation?.conversationID,
                        )
                    ) {
                        eventBus.emit("show-message", {
                            message: t("chat.clear-history-empty-tips"),
                            type: "warning",
                        });
                        return;
                    }
                    setCustomContent({
                        key: "ClearAllMsg",
                        title: t("chat.clear-history-header", { user: conversation.userName }),
                        descPrev: t("chat.clear-history-prev", { user: conversation.userName }),
                        descNext: t("chat.clear-history-next"),
                    });
                    setShowCustomModal(true);
                }
            },
        },
        {
            label: conversation.isPinned ? t("chat.cancel-pin-friend") : t("chat.pin-friend"),
            key: "PinFriend",
            showType: [ContactType.Friend],
            method: async () => {
                if (conversation?.conversationID) {
                    await rtm.pinConversation(conversation.conversationID, !conversation.isPinned);
                }
            },
        },
        {
            label: t("chat.denounce"),
            key: "Denounce",
            showType: [ContactType.Friend, ContactType.Stranger],
            method: () => {
                setShowReport(true);
            },
        },
        {
            label:
                conversation.messageRemindType === "Discard"
                    ? t("chat.cancel-block-msg")
                    : t("chat.block-msg"),
            key: "BlockMsg",
            showType: [ContactType.Friend, ContactType.Stranger],
            method: async () => {
                const curType =
                    conversation.messageRemindType === "Discard" ? "AcceptAndNotify" : "Discard";
                if (curType === "Discard") {
                    setCustomContent({
                        key: "BlockMsg",
                        title: t("chat.block-user-header", { user: conversation.userName }),
                        descPrev: t("chat.block-user-content-prev", {
                            user: conversation.userName,
                        }),
                        descNext: t("chat.block-user-content-next"),
                    });
                    setShowCustomModal(true);
                } else if (conversation?.userID) {
                    await rtm.setMsgRemindType([conversation.userID], curType);
                    await rtm.removeFromBlacklist([conversation.userID]); // 从黑名单移除
                    // 取消屏蔽
                    const res = await cancelBlock(conversation.userID);
                    if (res.code === "200") {
                        updateBlockedUsers(res.data || []);
                        const tempConversation = localeConversation.find(
                            e => e.conversationID === conversation.conversationID,
                        );
                        if (tempConversation) {
                            updateLocaleConversation(
                                { ...tempConversation, messageRemindType: curType },
                                "update",
                            );
                        }
                    }
                }
            },
        },
    ];

    const handleConfirmCustom = async (): Promise<void> => {
        if (customContent.key === "BlockMsg") {
            const curType =
                conversation.messageRemindType === "Discard" ? "AcceptAndNotify" : "Discard";
            if (conversation?.userID) {
                await rtm.setMsgRemindType([conversation.userID], curType);
                await rtm.addToBlacklist([conversation.userID]); // 加入黑名单
                // 屏蔽某人
                const res = await blockFriend(conversation.userID);
                if (res.code === "200") {
                    updateBlockedUsers(res.data || []);
                }
                const tempConversation = localeConversation.find(
                    e => e.conversationID === conversation.conversationID,
                );
                if (tempConversation) {
                    updateLocaleConversation(
                        { ...tempConversation, messageRemindType: curType },
                        "update",
                    );
                }
            }
        } else if (customContent.key === "ClearAllMsg" && conversation.conversationID) {
            await rtm.deleteConversation(conversation.conversationID, true);
            // await rtm.clearHistoryMsg(conversation.conversationID);
            eventBus.emit("clear-history-msg", { userID: conversation.userID });
        }
        setShowCustomModal(false);
        setCustomContent({
            key: "",
            title: "",
            descPrev: "",
            descNext: "",
        });
    };

    useEffect(() => {
        const { conversationID, userID } = conversation;
        // 校验好友关系，将administrator默认为系统
        if (conversationID?.startsWith("C2C") && userID !== "administrator") {
            checkFriend(conversation);
        } else {
            setCurType(ContactType.System);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation]);

    const checkFriend = async (conversation: ContactItem): Promise<void> => {
        if (userInfo) {
            const res = await rtm.checkFriend([userInfo.user_id, conversation.userID]);
            setCurType(res ? ContactType.Friend : ContactType.Stranger);
        }
    };

    const onClick = ({ key }: { key: string }): void => {
        const curItem = menuItem.find(e => e.key === key);
        curItem?.method();
        onClickMenu && onClickMenu(false);
    };

    const confirmToInvite = async (channelKey: string, channelUUID: string): Promise<void> => {
        const { data: invite_flag } = await setInviteURLTime(channelKey, channelUUID);
        updateChannelInviteFlag(channelUUID, invite_flag);
        const payload: ICustomPayload = {
            data: `${REMO_GAME_WEB_URL}#/channel/invite/${invite_flag}`,
            description: ICustomPayloadDescription.Link,
        };
        await rtm.sendPeerMessage(CustomType.Custom, conversation.userID, payload);
        eventBus.emit("send-new-msg", { userID: conversation.userID });
        eventBus.emit("show-message", {
            message: t("channel.invite-friend-success"),
            type: "success",
        });
        setShowInvite(false);
    };
    return (
        <>
            <Dropdown
                getPopupContainer={() => document.body}
                menu={{
                    items: menuItem
                        .filter(
                            e =>
                                (curType && e.showType.includes(curType)) ||
                                (e.key === "PinFriend" && conversation.isPinned === true),
                        )
                        .map(item => ({
                            key: item.key,
                            label: item.label,
                        })),
                    onClick: onClick,
                    onMouseLeave: () => onClickMenu && onClickMenu(false),
                }}
                open={open}
                overlayStyle={{
                    position: "fixed",
                    left: menuPosition.x,
                    top: menuPosition.y,
                }}
                rootClassName="private-right-menu-popover"
                trigger={["contextMenu"]}
            ></Dropdown>
            {showInvite && (
                <InviteToChannel onClose={() => setShowInvite(false)} onConfirm={confirmToInvite} />
            )}
            {showReport && conversation.userName && (
                <ReportModal
                    setVisible={setShowReport}
                    userID={conversation.userID}
                    userName={conversation.userName}
                    visible={showReport}
                />
            )}
            {showCustomModal && (
                <CustomModal
                    className="block-modal"
                    footer={
                        <div className="modal-button-group">
                            <div
                                className="modal-button-item button-normal"
                                onClick={() => {
                                    setCustomContent({
                                        key: "",
                                        title: "",
                                        descPrev: "",
                                        descNext: "",
                                    });
                                    setShowCustomModal(false);
                                }}
                            >
                                {t("cancel")}
                            </div>
                            <div
                                className="modal-button-item button-danger"
                                onClick={handleConfirmCustom}
                            >
                                {t("confirm")}
                            </div>
                        </div>
                    }
                    open={showCustomModal}
                    renderHeader={customContent.title}
                    setVisible={setShowCustomModal}
                >
                    <div className="block-modal-content">
                        {customContent.descPrev}
                        {/* <b style={{ margin: "0 4px" }}>{conversation.userName}</b> */}
                        {customContent.descNext}
                    </div>
                </CustomModal>
            )}
        </>
    );
};
